import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatForwardTarget, formatFileSize } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

/**
 * "Forward to…" — pick one or more people or groups and send a copy of a message to each.
 *
 * Opened from a bubble's hover menu and rendered at the top of the page, with the message itself
 * held in {@link ChatStore.forwardSource} rather than passed in: the two ends are four components
 * apart and neither owns this dialog.
 *
 * Multi-select on purpose. Forwarding one thing to three people is the common case, and doing it
 * as three separate trips through a single-select picker is the annoying version of the same
 * feature.
 */
@Component({
  selector: 'app-chat-forward-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatAvatarComponent],
  templateUrl: './chat-forward-dialog.component.html',
  styleUrls: ['./chat-forward-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatForwardDialogComponent {
  readonly store = inject(ChatStore);
  readonly formatFileSize = formatFileSize;

  readonly search = signal('');

  /**
   * Selection is keyed by conversation id where there is one and user id otherwise — a colleague
   * you have never messaged has no conversation to key on, and would otherwise collide with every
   * other such colleague on a null key.
   */
  private readonly selectedKeys = signal(new Set<string>());

  readonly message = this.store.forwardSource;

  readonly filtered = computed<ChatForwardTarget[]>(() => {
    const term = this.search().trim().toLowerCase();
    const targets = this.store.forwardTargets();
    if (!term) {
      return targets;
    }
    return targets.filter(target => target.name.toLowerCase().includes(term) || (target.subtitle ?? '').toLowerCase().includes(term));
  });

  readonly selected = computed<ChatForwardTarget[]>(() => {
    const keys = this.selectedKeys();
    return this.store.forwardTargets().filter(target => keys.has(this.keyOf(target)));
  });

  readonly selectedCount = computed(() => this.selectedKeys().size);

  keyOf(target: ChatForwardTarget): string {
    return target.conversation_id ?? `user:${target.user_id}`;
  }

  isSelected(target: ChatForwardTarget): boolean {
    return this.selectedKeys().has(this.keyOf(target));
  }

  toggle(target: ChatForwardTarget): void {
    const key = this.keyOf(target);
    this.selectedKeys.update(keys => {
      // A new Set each time: signals compare by reference, and mutating in place would not notify.
      const next = new Set(keys);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }

  // ---- The preview -----------------------------------------------------------

  /** One line describing what is being forwarded, for the card at the top of the dialog. */
  get previewText(): string {
    const message = this.message();
    if (!message) {
      return '';
    }
    if (message.body?.trim()) {
      return message.body;
    }
    const first = message.attachments?.[0];
    return first ? first.name : 'Message';
  }

  get attachmentCount(): number {
    return this.message()?.attachments?.length ?? 0;
  }

  /**
   * Warns before the fact that attachments are copied, not linked.
   *
   * They are re-uploaded into each target conversation, because the server binds an attachment to
   * the conversation it is posted into — so a very large file forwarded to five groups really is
   * five uploads, and saying so beforehand beats a surprised user watching a spinner.
   */
  get attachmentNote(): string {
    const attachments = this.message()?.attachments ?? [];
    if (!attachments.length) {
      return '';
    }
    const bytes = attachments.reduce((total, attachment) => total + (attachment.size || 0), 0);
    const label = attachments.length === 1 ? '1 attachment' : `${attachments.length} attachments`;
    return `${label} (${formatFileSize(bytes)}) will be uploaded again for each conversation.`;
  }

  // ---- Actions ---------------------------------------------------------------

  send(): void {
    const message = this.message();
    const targets = this.selected();
    if (!message || !targets.length || this.store.forwarding()) {
      return;
    }
    this.store.forwardMessage(message, targets);
  }

  close(): void {
    if (!this.store.forwarding()) {
      this.store.cancelForward();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  trackByTarget = (_: number, target: ChatForwardTarget) => this.keyOf(target);
}
